<template>
  <el-dialog
    :title="'开放时间表 · ' + title"
    :visible.sync="innerVisible"
    width="520px"
    class="share-dialog"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <div v-loading="loading" class="share-body">
      <div v-if="!loading && !friends.length" class="share-empty">
        你还没有好友，先在「朋友」页面添加好友后才能开放时间表。
      </div>

      <template v-else>
        <p class="share-tip">选择好友并授权时间表权限</p>

        <div class="friend-list">
          <div
            v-for="f in friends"
            :key="f.friendId"
            class="friend-row"
            :class="{ granted: !!selected[String(f.friendId)] }"
          >
            <div class="friend-main">
              <UserAvatar class="friend-avatar" :url="f.avatarUrl" :name="displayName(f)" />
              <span class="friend-name" :title="displayName(f)">{{ displayName(f) }}</span>
              <input
                type="checkbox"
                class="friend-check"
                :checked="!!selected[String(f.friendId)]"
                :title="selected[String(f.friendId)] ? '取消开放' : '开放给该好友'"
                @change="toggleFriend(f, $event.target.checked)"
              >
            </div>
            <div v-if="selected[String(f.friendId)]" class="friend-perms">
              <div class="seg seg-sm">
                <button
                  type="button"
                  class="seg-btn"
                  :class="{ active: selected[String(f.friendId)].permission === 'READ' }"
                  @click="setPermission(f.friendId, 'READ')"
                >只读</button>
                <button
                  type="button"
                  class="seg-btn"
                  :class="{ active: selected[String(f.friendId)].permission === 'EDIT' }"
                  @click="setPermission(f.friendId, 'EDIT')"
                >编辑</button>
              </div>
              <!-- 「分类（周累计）」统计表的查看权限：与只读 / 编辑相互独立 -->
              <label class="cat-toggle" :class="{ on: selected[String(f.friendId)].categoryVisible }">
                <input
                  type="checkbox"
                  class="cat-check"
                  :checked="selected[String(f.friendId)].categoryVisible"
                  @change="setCategoryVisible(f.friendId, $event.target.checked)"
                >
                <span>开放周累计表</span>
              </label>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div slot="footer" class="share-footer">
      <el-button size="small" @click="innerVisible = false">取消</el-button>
      <el-button
        size="small"
        type="primary"
        :loading="saving"
        :disabled="loading || !friends.length"
        @click="save"
      >保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import UserAvatar from '@/components/common/UserAvatar.vue'
import { listFriends } from '@/api/friend.js'
import {
  listGrants,
  grantProject,
  revokeGrant
} from '@/api/share.js'

export default {
  name: 'ShareDialog',
  components: { UserAvatar },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: [String, Number],
      default: null
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      innerVisible: false,
      loading: false,
      saving: false,
      friends: [],
      /**
       * friendId(string) -> { permission: 'READ'|'EDIT', categoryVisible: boolean }
       * 不在这个对象里的好友 = 不开放。
       */
      selected: {},
      /** 打开时后端已有的授权，形状同 selected，用于 diff */
      existing: {}
    }
  },
  watch: {
    visible(v) {
      if (v && !this.innerVisible) {
        this.innerVisible = true
        this.load()
      } else if (!v) {
        this.innerVisible = false
      }
    },
    innerVisible(v) {
      if (!v) this.$emit('update:visible', false)
    }
  },
  methods: {
    displayName(f) {
      return f.remark || f.nickname || '好友'
    },
    async load() {
      if (!this.projectId) return
      this.loading = true
      try {
        const [fRes, gRes] = await Promise.all([
          listFriends(),
          listGrants(this.projectId)
        ])
        this.friends = (fRes.data || []).map(f => ({
          friendId: f.friendId,
          nickname: f.nickname,
          remark: f.remark,
          avatarUrl: f.avatarUrl
        }))
        this.existing = {}
        this.selected = {}
        ;(gRes.data || []).forEach(g => {
          const id = String(g.friendId)
          const entry = {
            permission: g.permission === 'EDIT' ? 'EDIT' : 'READ',
            categoryVisible: !!g.categoryVisible
          }
          this.existing[id] = entry
          // 必须用 $set：直接 this.selected[id] = entry 会让属性脱离响应式，
          // 后续 setCategoryVisible 里 this.$set 替换时 Vue 会以「key 已存在」跳过
          // observe / notify（vue 2.6 set 函数的 fast path），checkbox 就「点不动」。
          this.$set(this.selected, id, { ...entry })
        })
      } catch (e) {
        console.error('[ShareDialog] 加载好友 / 授权失败', e)
        this.friends = []
        this.existing = {}
        this.selected = {}
      } finally {
        this.loading = false
      }
    },
    toggleFriend(f, checked) {
      const id = String(f.friendId)
      if (checked) {
        // 新勾选默认「只读 + 不开放周累计表」，由用户按需再调整
        this.$set(this.selected, id, {
          permission: 'READ',
          categoryVisible: false
        })
      } else {
        this.$delete(this.selected, id)
      }
    },
    setPermission(friendId, permission) {
      const id = String(friendId)
      const entry = this.selected[id]
      if (!entry) return
      this.$set(this.selected, id, { ...entry, permission })
    },
    setCategoryVisible(friendId, categoryVisible) {
      const id = String(friendId)
      const entry = this.selected[id]
      if (!entry) return
      this.$set(this.selected, id, { ...entry, categoryVisible: !!categoryVisible })
    },
    async save() {
      if (!this.projectId) return
      const desired = this.selected
      const existing = this.existing
      const ops = []
      // 新增 / 变更权限（含分类统计表权限）
      Object.keys(desired).forEach(id => {
        const want = desired[id]
        const had = existing[id]
        if (!had || had.permission !== want.permission || had.categoryVisible !== want.categoryVisible) {
          ops.push(grantProject(this.projectId, id, want.permission, want.categoryVisible))
        }
      })
      // 撤销（之前有、现在没选）
      Object.keys(existing).forEach(id => {
        if (!desired[id]) {
          ops.push(revokeGrant(this.projectId, id))
        }
      })
      if (!ops.length) {
        this.innerVisible = false
        return
      }
      this.saving = true
      try {
        const results = await Promise.all(ops.map(p => p.catch(e => e)))
        const failed = results.filter(r => r && (r.success === false || r.code !== 200 || r instanceof Error))
        if (failed.length) {
          const msg = failed[0] && failed[0].message ? failed[0].message : '部分授权操作失败'
          this.$message.error(msg)
          // 重新加载，保证界面与后端一致
          await this.load()
          return
        }
        this.$message.success('已更新开放权限')
        this.$emit('changed')
        this.innerVisible = false
      } catch (e) {
        console.error('[ShareDialog] 保存授权失败', e)
        this.$message.error('保存失败，请稍后重试')
      } finally {
        this.saving = false
      }
    },
    onClosed() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.share-body {
  min-height: 120px;
}
.share-empty {
  padding: 28px 8px;
  text-align: center;
  color: var(--lp-text-3, #94a59b);
  font-size: 13px;
  line-height: 1.6;
}
/* 权限设置说明小字（原「新选好友默认权限」条已按用户要求删除） */
.share-tip {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--lp-text-3, #94a59b);
}
.seg {
  display: inline-flex;
  border: 1px solid var(--lp-border-strong, #a5cdb3);
  border-radius: 0.5rem;
  overflow: hidden;
}
.seg-btn {
  border: none;
  background: var(--lp-surface, #fff);
  color: var(--lp-text-2, #5f7268);
  padding: 4px 12px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.seg-btn + .seg-btn {
  border-left: 1px solid var(--lp-border-strong, #a5cdb3);
}
.seg-btn.active {
  background: var(--lp-primary, #4aa46a);
  color: #fff;
}
.seg-sm .seg-btn {
  padding: 2px 9px;
  font-size: 11px;
}
.friend-list {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 0.625rem;
}
.friend-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--lp-border-soft, #eef5f0);
}
.friend-row:last-child {
  border-bottom: none;
}
.friend-row:hover {
  background: var(--lp-surface-2, #f7fbf8);
}
.friend-main {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* 已授权行：给整行一层浅底，一眼看出哪些好友已经开放 */
.friend-row.granted {
  background: var(--lp-surface-2, #f7fbf8);
}
.friend-perms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding-left: 40px;
}
/* 「分类（周累计）」统计表查看权限：与只读 / 编辑相互独立的小开关 */
.cat-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--lp-text-2, #5f7268);
  cursor: pointer;
  user-select: none;
}
.cat-toggle.on {
  color: var(--lp-primary-dark, #2f7a4a);
  font-weight: 600;
}
.cat-check {
  width: 15px;
  height: 15px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: var(--lp-primary, #4aa46a);
}
.friend-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--lp-primary, #4aa46a);
  border: 2px solid var(--lp-primary-dark, #2f7a4a);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.friend-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--lp-text, #26332b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.friend-check {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: var(--lp-primary, #4aa46a);
}
.share-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
